import { useCallback, useState } from "react";
import {
  assignCmsAdminTourRequest,
  assignCmsUserAdminRoleRequest,
  getCmsUserDetailRequest,
  exportCmsUserRequest,
  listCmsUsersRequest,
  removeCmsAdminTourRequest,
  removeCmsUserAdminRoleRequest,
} from "@services/cmsUsers/cmsUsersApi";

const initialPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 1,
};

function useCmsUsers() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [roleSubmitting, setRoleSubmitting] = useState(false);
  const [tourAccessSubmittingId, setTourAccessSubmittingId] = useState("");
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const syncUserRoles = useCallback((updatedUser) => {
    if (!updatedUser?.id) return;
    setSelectedUser((currentUser) => {
      if (!currentUser || currentUser.id !== updatedUser.id) return currentUser;
      return {
        ...currentUser,
        ...updatedUser,
        roles: Array.isArray(updatedUser.roles) ? updatedUser.roles : currentUser.roles,
      };
    });
    setUsers((currentUsers) =>
      currentUsers.map((user) =>
        user.id === updatedUser.id
          ? {
              ...user,
              ...updatedUser,
              roles: Array.isArray(updatedUser.roles) ? updatedUser.roles : user.roles,
            }
          : user,
      ),
    );
  }, []);

  const fetchUsers = useCallback(async ({ page = 1, limit = 20, search = "", role = "all" } = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await listCmsUsersRequest({ page, limit, search, role });
      setUsers(response.body.users);
      setPagination(response.body.pagination);
    } catch (err) {
      setError(err.message);
      setUsers([]);
      setPagination(initialPagination);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setError("");
    try {
      const response = await getCmsUserDetailRequest(id);
      setSelectedUser(response.body);
    } catch (err) {
      setError(err.message);
      setSelectedUser(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const exportUser = useCallback(async (id, format = "csv") => {
    if (!id) return;
    setExporting(true);
    setError("");
    try {
      return await exportCmsUserRequest(id, format);
    } catch (err) {
      setError(err.message || "Could not export user information.");
      throw err;
    } finally {
      setExporting(false);
    }
  }, []);

  const assignAdminRole = useCallback(async (id) => {
    if (!id) return;
    setRoleSubmitting(true);
    setError("");
    try {
      const response = await assignCmsUserAdminRoleRequest(id);
      syncUserRoles(response.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setRoleSubmitting(false);
    }
  }, [syncUserRoles]);

  const removeAdminRole = useCallback(async (id) => {
    if (!id) return;
    setRoleSubmitting(true);
    setError("");
    try {
      const response = await removeCmsUserAdminRoleRequest(id);
      syncUserRoles(response.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setRoleSubmitting(false);
    }
  }, [syncUserRoles]);

  const assignAdminTour = useCallback(async (id, tourId) => {
    if (!id || !tourId) return;
    setTourAccessSubmittingId(String(tourId));
    setError("");
    try {
      const response = await assignCmsAdminTourRequest(id, tourId);
      syncUserRoles(response.body);
      const detailResponse = await getCmsUserDetailRequest(id);
      setSelectedUser(detailResponse.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setTourAccessSubmittingId("");
    }
  }, [syncUserRoles]);

  const removeAdminTour = useCallback(async (id, tourId) => {
    if (!id || !tourId) return;
    setTourAccessSubmittingId(String(tourId));
    setError("");
    try {
      const response = await removeCmsAdminTourRequest(id, tourId);
      syncUserRoles(response.body);
      const detailResponse = await getCmsUserDetailRequest(id);
      setSelectedUser(detailResponse.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setTourAccessSubmittingId("");
    }
  }, [syncUserRoles]);

  return {
    users,
    pagination,
    selectedUser,
    loading,
    detailLoading,
    exporting,
    roleSubmitting,
    tourAccessSubmittingId,
    error,
    fetchUsers,
    fetchUserDetail,
    exportUser,
    assignAdminRole,
    removeAdminRole,
    assignAdminTour,
    removeAdminTour,
    clearSelectedUser: () => setSelectedUser(null),
  };
}

export default useCmsUsers;
